import type { ReactElement } from "react"
import React from "react"
import { useTranslation } from "react-i18next"

import { BillPdfViewerDialog } from "@/components/bill-pdf-viewer-dialog"
import { NzDataGrid, defaultRowHeight } from "@/components/nzDataGrid"
import { BillPdfPreviewDetailDialog } from "@/modules/data/components/bill-pdf-preview-detail-dialog"
import { BillPdfPreviewEditDialog } from "@/modules/data/components/bill-pdf-preview-edit-dialog"
import type {
  BillPdfPreviewRow,
  BillPdfPreviewRowEdit,
  BillPdfRecommendationBand,
  BillPdfUploadFileStatus,
} from "@/modules/data/services/use-bill-pdf-upload-session"
import {
  type BillPdfFileState,
  getBillPdfFileStateLabel,
  isFinishedBillPdfFileState,
  isUnfinishedBillPdfFileState,
  isUnsavedPreviewRow,
} from "@/modules/data/utils/bill-pdf-preview-state"
import { GridActionTooltipButton, baseColumnDef } from "@/nzds/data-grid"
import { getDisplayValue } from "@/utils/value"

import DeleteIcon from "@mui/icons-material/Delete"
import FileOpenIcon from "@mui/icons-material/FileOpen"
import type { ChipProps } from "@mui/material"
import { Box, Chip } from "@mui/material"
import type {
  GridActionsCellItemProps,
  GridColDef,
  GridRowId,
} from "@mui/x-data-grid-pro"
import {
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridActionsCellItem,
} from "@mui/x-data-grid-pro"

import {
  billPdfPreviewActionsColumnWidth,
  billPdfPreviewNestedGridInsetPx,
} from "./bill-pdf-preview-actions-column-width"
import { BillPdfPreviewBillsPanel } from "./bill-pdf-preview-bills-panel"
import { BillPdfRecommendationChip } from "./bill-pdf-recommendation-chip"

interface BillPdfPreviewGridProps {
  files: BillPdfUploadFileStatus[]
  onRemove?: (rowId: string) => void
  onUpdate?: (rowId: string, edit: BillPdfPreviewRowEdit) => Promise<void>
  refreshFiles?: () => Promise<BillPdfUploadFileStatus[]>
  rows: BillPdfPreviewRow[]
}

interface BillPdfPreviewSource {
  downloadUrl: string
  filename: string
}

interface BillPdfFileRow {
  file: BillPdfUploadFileStatus
  id: string
  previewRows: BillPdfPreviewRow[]
}

interface FileRowActionLabels {
  remove: string
  unavailableReason: string
  viewPdf: string
}

interface FileRowActionsOptions {
  labels: FileRowActionLabels
  onRemove?: (rowId: string) => void
  onViewPdf: (row: BillPdfFileRow) => void
  row: BillPdfFileRow
  source: BillPdfPreviewSource | null
}

interface BillPdfFileDetailPanelOptions {
  onEdit?: (row: BillPdfPreviewRow) => void
  onRemove?: (rowId: string) => void
  onView: (row: BillPdfPreviewRow) => void
}

const groupPreviewRowsByFileId = (
  rows: BillPdfPreviewRow[]
): Map<string, BillPdfPreviewRow[]> => {
  const grouped = new Map<string, BillPdfPreviewRow[]>()

  for (const row of rows) {
    const fileId = row.sourceFileId?.trim()
    if (!fileId) {
      continue
    }

    const current = grouped.get(fileId)
    if (current) {
      current.push(row)
    } else {
      grouped.set(fileId, [row])
    }
  }

  return grouped
}

const buildBillPdfFileRows = (
  files: BillPdfUploadFileStatus[],
  rows: BillPdfPreviewRow[]
): BillPdfFileRow[] => {
  const rowsByFileId = groupPreviewRowsByFileId(rows)
  return files.flatMap((file, index) => {
    const fileId = file.id?.trim() ?? ""
    const previewRows = fileId === "" ? [] : (rowsByFileId.get(fileId) ?? [])
    if (!isUnfinishedBillPdfFileState(file.state) && previewRows.length === 0) {
      return []
    }

    return [{ file, id: fileId === "" ? `file-${index}` : fileId, previewRows }]
  })
}

const getFilePreviewSource = (
  file: BillPdfUploadFileStatus
): BillPdfPreviewSource | null => {
  const downloadUrl = file.downloadUrl?.trim()
  if (!downloadUrl) {
    return null
  }

  try {
    new URL(downloadUrl)
  } catch {
    return null
  }

  return {
    downloadUrl,
    filename: file.filename.trim(),
  }
}

const FILE_STATE_CHIP_COLORS: Record<
  Extract<BillPdfFileState, "failed" | "parsed">,
  ChipProps["color"]
> = {
  failed: "error",
  parsed: "success",
}

const getFileStateChipColor = (state: string): ChipProps["color"] => {
  if (state === "failed" || state === "parsed") {
    return FILE_STATE_CHIP_COLORS[state]
  }

  return "default"
}

// The most cautious bill wins. Bills without a recommendation (for example,
// saved ones) are ignored. Proceed needs every remaining bill to be Proceed.
const getFileRecommendationBand = (
  previewRows: BillPdfPreviewRow[]
): BillPdfRecommendationBand | null => {
  const bands = previewRows.flatMap((row) =>
    row.recommendation ? [row.recommendation.band] : []
  )
  if (bands.includes("reject")) {
    return "reject"
  }
  if (bands.includes("review")) {
    return "review"
  }

  return bands.length > 0 && bands.every((band) => band === "proceed")
    ? "proceed"
    : null
}

const getFinishedFileIds = (fileRows: BillPdfFileRow[]): string[] =>
  fileRows
    .filter((fileRow) => isFinishedBillPdfFileState(fileRow.file.state))
    .map((fileRow) => fileRow.id)

const getUnsavedPreviewRowIds = (row: BillPdfFileRow): string[] =>
  row.previewRows
    .filter((previewRow) => isUnsavedPreviewRow(previewRow))
    .map((previewRow) => previewRow.id)

const areSameIds = (
  left: ReadonlySet<string>,
  right: ReadonlySet<string>
): boolean => {
  if (left.size !== right.size) {
    return false
  }

  for (const id of left) {
    if (!right.has(id)) {
      return false
    }
  }

  return true
}

const getFileViewPdfAction = (
  row: BillPdfFileRow,
  source: BillPdfPreviewSource | null,
  onViewPdf: (row: BillPdfFileRow) => void,
  labels: FileRowActionLabels
): ReactElement<GridActionsCellItemProps> => (
  <GridActionsCellItem
    component={GridActionTooltipButton}
    disabled={source == null}
    icon={<FileOpenIcon color="primary" />}
    key="view-pdf-action"
    label={
      source == null
        ? `${labels.viewPdf}: ${labels.unavailableReason}`
        : labels.viewPdf
    }
    onClick={() => {
      onViewPdf(row)
    }}
    showInMenu={false}
  />
)

const getFileRowActions = ({
  labels,
  onRemove,
  onViewPdf,
  row,
  source,
}: FileRowActionsOptions): ReactElement<GridActionsCellItemProps>[] => {
  if (!isFinishedBillPdfFileState(row.file.state)) {
    return []
  }

  const removableRowIds = getUnsavedPreviewRowIds(row)
  const actions: ReactElement<GridActionsCellItemProps>[] = [
    getFileViewPdfAction(row, source, onViewPdf, labels),
  ]

  if (onRemove && removableRowIds.length > 0) {
    actions.push(
      <GridActionsCellItem
        component={GridActionTooltipButton}
        icon={<DeleteIcon />}
        key="remove-action"
        label={labels.remove}
        onClick={() => {
          for (const rowId of removableRowIds) {
            onRemove(rowId)
          }
        }}
        showInMenu={false}
      />
    )
  }

  return actions
}

const getBillPdfFileDetailPanelContent =
  ({ onEdit, onRemove, onView }: BillPdfFileDetailPanelOptions) =>
  ({ row }: { row: BillPdfFileRow }): ReactElement | null => {
    if (!isFinishedBillPdfFileState(row.file.state)) {
      return null
    }

    return (
      <BillPdfPreviewBillsPanel
        label={getDisplayValue(row.file.filename)}
        onEdit={onEdit}
        onRemove={onRemove}
        onView={onView}
        rows={row.previewRows}
      />
    )
  }

export const BillPdfPreviewGrid: React.FC<BillPdfPreviewGridProps> = ({
  files,
  onRemove,
  onUpdate,
  refreshFiles,
  rows,
}) => {
  const { t } = useTranslation("data")
  const { t: tCommon } = useTranslation("common")
  const [collapsedFileIds, setCollapsedFileIds] = React.useState<Set<string>>(
    () => new Set()
  )
  const [viewerSource, setViewerSource] =
    React.useState<BillPdfPreviewSource | null>(null)
  const [detailRow, setDetailRow] = React.useState<BillPdfPreviewRow | null>(
    null
  )
  const [editRow, setEditRow] = React.useState<BillPdfPreviewRow | null>(null)
  const fileRows = React.useMemo(
    () => buildBillPdfFileRows(files, rows),
    [files, rows]
  )
  const expandedRowIds = React.useMemo(
    () =>
      new Set<GridRowId>(
        getFinishedFileIds(fileRows).filter((id) => !collapsedFileIds.has(id))
      ),
    [collapsedFileIds, fileRows]
  )
  const viewerFilename = viewerSource?.filename.trim() ?? ""
  const actionsColumnWidth =
    billPdfPreviewActionsColumnWidth(onUpdate != null || onRemove != null) +
    billPdfPreviewNestedGridInsetPx
  const fileActionLabels: FileRowActionLabels = {
    remove: tCommon("button.remove"),
    unavailableReason: t("upload.billPdf.viewUnavailable"),
    viewPdf: tCommon("button.viewPdf"),
  }
  const getDetailPanelContent = React.useMemo(
    () =>
      getBillPdfFileDetailPanelContent({
        onEdit: onUpdate ? setEditRow : undefined,
        onRemove,
        onView: setDetailRow,
      }),
    [onRemove, onUpdate]
  )

  const handleExpandedRowIdsChange = (ids: Set<GridRowId>): void => {
    const nextCollapsed = new Set(
      getFinishedFileIds(fileRows).filter((id) => !ids.has(id))
    )
    setCollapsedFileIds((current) =>
      areSameIds(current, nextCollapsed) ? current : nextCollapsed
    )
  }

  const handleViewPdf = (row: BillPdfFileRow): void => {
    const openViewer = async () => {
      let latestFiles = files
      if (refreshFiles) {
        try {
          latestFiles = await refreshFiles()
        } catch {
          latestFiles = files
        }
      }

      const latestFile =
        (row.file.id == null
          ? undefined
          : latestFiles.find((candidate) => candidate.id === row.file.id)) ??
        row.file
      setViewerSource(
        getFilePreviewSource(latestFile) ?? getFilePreviewSource(row.file)
      )
    }

    void openViewer()
  }

  const columns: GridColDef<BillPdfFileRow>[] = [
    {
      ...baseColumnDef,
      field: "filename",
      filterable: false,
      flex: 1,
      headerName: t("upload.billPdf.columnFile"),
      minWidth: 180,
      sortable: false,
      valueGetter: (_value, row) => getDisplayValue(row.file.filename),
    },
    {
      ...baseColumnDef,
      field: "state",
      filterable: false,
      headerName: t("upload.billPdf.columnStatus"),
      minWidth: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Chip
          color={getFileStateChipColor(row.file.state)}
          label={getBillPdfFileStateLabel(row.file.state, t)}
          size="small"
          variant="outlined"
        />
      ),
      valueGetter: (_value, row) => getBillPdfFileStateLabel(row.file.state, t),
      width: 140,
    },
    {
      ...baseColumnDef,
      field: "recommendation",
      filterable: false,
      headerName: t("upload.billPdf.columnRecommendation"),
      minWidth: 120,
      sortable: false,
      renderCell: ({ row }) => {
        const band = getFileRecommendationBand(row.previewRows)
        return band ? (
          <BillPdfRecommendationChip band={band} />
        ) : (
          getDisplayValue(null)
        )
      },
      valueGetter: (_value, row) => getFileRecommendationBand(row.previewRows),
      width: 140,
    },
    {
      ...baseColumnDef,
      field: "billCount",
      filterable: false,
      headerName: t("upload.billPdf.columnBillCount"),
      minWidth: 90,
      sortable: false,
      type: "number",
      valueGetter: (_value, row) => row.previewRows.length,
      width: 110,
    },
    {
      ...baseColumnDef,
      align: "left",
      cellClassName: "actions",
      field: "actions",
      filterable: false,
      getActions: ({ row }) =>
        getFileRowActions({
          labels: fileActionLabels,
          onRemove,
          onViewPdf: handleViewPdf,
          row,
          source: getFilePreviewSource(row.file),
        }),
      headerAlign: "left",
      headerName: tCommon("gridColumn.actions.name"),
      minWidth: actionsColumnWidth,
      sortable: false,
      type: "actions",
      width: actionsColumnWidth,
    },
  ]

  return (
    <Box data-testid="bill-pdf-preview-grid" sx={{ width: "100%" }}>
      <NzDataGrid
        aria-label={t("upload.billPdf.gridTitle")}
        autoHeight
        columnHeaderHeight={defaultRowHeight}
        columns={columns}
        detailPanelExpandedRowIds={expandedRowIds}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick
        disableVirtualization
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={() => "auto"}
        getRowId={(row: BillPdfFileRow) => row.id}
        hideFooter
        onDetailPanelExpandedRowIdsChange={handleExpandedRowIdsChange}
        pinnedColumns={{ right: ["actions"] }}
        rows={fileRows}
        sx={{
          // The toggle button is wider than its 40px cell and the overflow renders as ".."
          [`& .MuiDataGrid-cell[data-field="${GRID_DETAIL_PANEL_TOGGLE_FIELD}"]`]:
            { overflow: "visible", textOverflow: "clip" },
        }}
      />
      <BillPdfViewerDialog
        downloadUrl={viewerSource?.downloadUrl ?? ""}
        iframeTitle={t("upload.billPdf.iframeTitle")}
        onClose={() => {
          setViewerSource(null)
        }}
        open={viewerSource != null}
        title={
          viewerFilename !== "" ? viewerFilename : t("upload.billPdf.gridTitle")
        }
      />
      {onUpdate ? (
        <BillPdfPreviewEditDialog
          onClose={() => {
            setEditRow(null)
          }}
          onSave={onUpdate}
          open={editRow != null}
          row={editRow}
        />
      ) : null}
      <BillPdfPreviewDetailDialog
        onClose={() => {
          setDetailRow(null)
        }}
        open={detailRow != null}
        row={detailRow}
      />
    </Box>
  )
}
